/**
 * Route analytics time windows (2026-09-29, Gabriel: "the three things …
 * you can build them" — the first was "windows": Routes was all-time only,
 * an admin could not ask "this term" or "last 30 days").
 *
 * A learned route's SCORE stays the profile's all-time figure — that is what
 * "learned" means. The window changes what the run columns count: how many
 * runs, how long and how far, inside the chosen dates, computed from the
 * bus's own completed runs matched back to the route.
 *
 * Matching mirrors the rebuild path (engine/route/reconcile.js): same bus,
 * same side, both endpoints within ANCHOR_TOLERANCE_M of the route's anchors.
 * Pure functions; no Firestore here.
 */
import { haversineMeters } from './fleetMapPath';

export type RouteWindowKey = 'term' | 'd30' | 'd90' | 'all';

/** Same as reconcile.js ROUTE_ANCHOR_TOLERANCE_M (150 m, Gabriel 2026-08-22). */
export const ANCHOR_TOLERANCE_M = 150;

export type RunSide = 'morning' | 'evening' | 'trip' | 'other';

export interface RunLite {
  id: string;
  busId: string;
  side: RunSide;
  startedAt: Date | null;
  finishedAt: Date | null;
  minutes: number | null;
  km: number | null;
  sLat: number | null;
  sLng: number | null;
  eLat: number | null;
  eLng: number | null;
  driverId: string | null;
}

export interface RouteAnchors {
  busId: string;
  side: RunSide;
  start: { lat: number; lng: number } | null;
  end: { lat: number; lng: number } | null;
}

export interface WindowBounds {
  key: RouteWindowKey;
  from: Date | null;
  to: Date | null;
  /** Short suffix for headers: "this term", "last 30 days", "all time". */
  label: string;
}

export interface WindowStats {
  runs: number;
  avgMinutes: number | null;
  avgKm: number | null;
  byDriver: Record<string, { avgMinutes: number; runs: number }>;
}

/** The side a run belongs to, by the same words the Routes tab reads on a profile. */
export function sideOfRouteType(routeType: string | null | undefined): RunSide {
  const t = String(routeType ?? '').toLowerCase();
  if (t.includes('school_trip')) return 'trip';
  if (t.includes('evening') || t.includes('return')) return 'evening';
  if (t.includes('morning') || t.includes('outbound')) return 'morning';
  return 'other';
}

function parseYmdLocal(ymd: string | null | undefined): Date | null {
  if (!ymd || !/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return null;
  const [y, m, d] = ymd.split('-').map(Number);
  const dt = new Date(y, m - 1, d, 0, 0, 0, 0);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

/**
 * Window dates for a key. `term` needs the org's term start (a YYYY-MM-DD);
 * without one it falls back to all time rather than inventing a term. The
 * term's end, when known, closes the window at the end of that day.
 */
export function windowBounds(
  key: RouteWindowKey,
  opts: { termStartYmd?: string | null; termEndYmd?: string | null; now?: Date } = {},
): WindowBounds {
  const now = opts.now ?? new Date();
  if (key === 'd30' || key === 'd90') {
    const days = key === 'd30' ? 30 : 90;
    const from = new Date(now.getTime() - days * 86400000);
    return { key, from, to: null, label: `last ${days} days` };
  }
  if (key === 'term') {
    const from = parseYmdLocal(opts.termStartYmd);
    if (!from) return { key: 'all', from: null, to: null, label: 'all time' };
    const endDay = parseYmdLocal(opts.termEndYmd);
    const to = endDay ? new Date(endDay.getTime() + 86400000 - 1) : null;
    return { key, from, to, label: 'this term' };
  }
  return { key: 'all', from: null, to: null, label: 'all time' };
}

/** A run is in the window by the moment it finished (or started, if never marked finished). */
export function runInWindow(run: RunLite, w: WindowBounds): boolean {
  const at = run.finishedAt ?? run.startedAt;
  if (!at) return false;
  if (w.from && at < w.from) return false;
  if (w.to && at > w.to) return false;
  return true;
}

/**
 * Does this run belong to this route? Same bus and side always; when the
 * route has anchors AND the run has endpoints, both ends must sit within the
 * tolerance. A route without anchors (legacy profile) matches on bus + side
 * alone; a run without endpoints (dead phone, no fixes) never matches an
 * anchored route — there is nothing to prove it drove that road.
 */
export function runMatchesRoute(run: RunLite, route: RouteAnchors, toleranceM = ANCHOR_TOLERANCE_M): boolean {
  if (run.busId !== route.busId || run.side !== route.side) return false;
  if (!route.start || !route.end) return true;
  const has = (n: number | null): n is number => typeof n === 'number' && Number.isFinite(n);
  if (!has(run.sLat) || !has(run.sLng) || !has(run.eLat) || !has(run.eLng)) return false;
  const sGap = haversineMeters({ lat: run.sLat, lng: run.sLng }, route.start);
  if (sGap > toleranceM) return false;
  const eGap = haversineMeters({ lat: run.eLat, lng: run.eLng }, route.end);
  return eGap <= toleranceM;
}

/** Runs of this route inside the window, reduced to the numbers the table shows. */
export function windowStats(runs: RunLite[], route: RouteAnchors, w: WindowBounds): WindowStats {
  const mine = runs.filter((r) => runInWindow(r, w) && runMatchesRoute(r, route));
  const good = (n: number | null): n is number => typeof n === 'number' && Number.isFinite(n) && n > 0;
  const mins = mine.map((r) => r.minutes).filter(good);
  const kms = mine.map((r) => r.km).filter(good);
  const avg = (xs: number[]) => (xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : null);
  const acc: Record<string, number[]> = {};
  for (const r of mine) {
    if (!r.driverId || !good(r.minutes)) continue;
    (acc[r.driverId] ||= []).push(r.minutes);
  }
  const byDriver: Record<string, { avgMinutes: number; runs: number }> = {};
  for (const [d, xs] of Object.entries(acc)) byDriver[d] = { avgMinutes: xs.reduce((a, b) => a + b, 0) / xs.length, runs: xs.length };
  return { runs: mine.length, avgMinutes: avg(mins), avgKm: avg(kms), byDriver };
}

/** A busAttendance document as the window counter needs it — classic
 *  BusAnalyticsPage's runLiteFromDoc, shared so the console reads runs the same
 *  way (2026-10-28). Null for anything that is not a finished school run (open,
 *  cancelled, a refuel drive). Endpoints: the learner's own `ri*` fields first,
 *  then the map pins the bus page uses, then the first and last breadcrumb. */
/** The fields runLiteFromData reads, for a read that brings back only those
 *  (console-v3/data/firestoreRest). routePoints is left out on purpose: it is
 *  the GPS trail, and only a run without the ri- or map- start and end fields
 *  falls back to it. */
export const RUN_LITE_FIELDS = [
  'busId', 'routeType', 'routeStartedAt', 'routeFinishedAt', 'durationMinutes', 'distanceKm', 'driverId', 'status', 'isRefuelTrip',
  'riStartLat', 'riStartLng', 'riEndLat', 'riEndLng', 'mapStartLat', 'mapStartLng', 'mapEndLat', 'mapEndLng',
];

export function runLiteFromData(id: string, data: Record<string, unknown>): RunLite | null {
  if (data.status !== 'completed') return null;
  if (data.isRefuelTrip === true || data.routeType === 'refuel') return null;
  const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);
  const ts = (v: unknown): Date | null => (v && typeof (v as { toDate?: () => Date }).toDate === 'function' ? (v as { toDate: () => Date }).toDate() : null);
  const pts = Array.isArray(data.routePoints) ? (data.routePoints as Array<{ lat?: unknown; lng?: unknown }>) : [];
  const first = pts[0];
  const last = pts[pts.length - 1];
  return {
    id,
    busId: String(data.busId || ''),
    side: sideOfRouteType(typeof data.routeType === 'string' ? data.routeType : null),
    startedAt: ts(data.routeStartedAt),
    finishedAt: ts(data.routeFinishedAt),
    minutes: num(data.durationMinutes),
    km: num(data.distanceKm),
    sLat: num(data.riStartLat) ?? num(data.mapStartLat) ?? (first ? num(first.lat) : null),
    sLng: num(data.riStartLng) ?? num(data.mapStartLng) ?? (first ? num(first.lng) : null),
    eLat: num(data.riEndLat) ?? num(data.mapEndLat) ?? (last ? num(last.lat) : null),
    eLng: num(data.riEndLng) ?? num(data.mapEndLng) ?? (last ? num(last.lng) : null),
    driverId: typeof data.driverId === 'string' ? data.driverId : null,
  };
}
